import { IHasTrackingParameters } from "./interfaces/IHasTrackingParameters";
import { IBasket } from "./interfaces/IBasket";
import { IBasketChanges } from "./interfaces/IBasketChanges";
import { IItemTrackingParameters } from "./interfaces/IItemTrackingParameters";
import IListNameAndId from "./interfaces/IListNameAndId";

export function extractTrackingInfoFromItemList(
	items: Array<IHasTrackingParameters>
): Array<any> {
	return items.map((item) => item?.globalTrackingParameters);
}

export function buildEcommerceSectionFromBasketDto(
	basketDto: IBasket,
	additionalEcommerceData: any = {}
): any {
	return {
		ecommerce: {
			...basketDto.globalTrackingParameters,
			...additionalEcommerceData,
			items: [
				...extractTrackingInfoFromItemList(basketDto?.paid || []),
				...extractTrackingInfoFromItemList(basketDto?.samples || []),
			],
		},
	};
}

export function buildEcommerceSectionFromBasketChangeDto(
	basketChangeDto: IBasketChanges,
	additionalEcommerceData: any = {}
) {
	return {
		ecommerce: {
			...basketChangeDto.globalTrackingParameters,
			...additionalEcommerceData,
			items: [...extractTrackingInfoFromItemList(basketChangeDto?.items || [])],
		},
	};
}

export function createEcommerceSectionFromItemTrackingParameters(
	itemTrackinParameters: IItemTrackingParameters,
	additionalEcommerceData: any = {}
) {
	return {
		ecommerce: {
			...itemTrackinParameters?.globalTrackingParameters,
			...additionalEcommerceData,
			items: [...itemTrackinParameters?.items],
		},
	};
}

export function buildEcommerceSectionFromItemListTrackingParameters({
	productsDto,
	observedProducts,
	itemListIdAndName,
}: {
	productsDto: IHasTrackingParameters;
	observedProducts: Array<IHasTrackingParameters>;
	itemListIdAndName: IListNameAndId;
}) {
	return {
		ecommerce: {
			...productsDto?.globalTrackingParameters,
			...itemListIdAndName,
			items: extractTrackingInfoFromItemList(observedProducts || []),
		},
	};
}

/**
 * Function checks if the GTM is enabled/disabled
 */
export function isGtmLoaded() {
	window.dataLayer = window.dataLayer || [];
	const gtmStartedEvent = window.dataLayer.find((element) => element['gtm.start']);

	if (!gtmStartedEvent) {
		// Not even the GTM inline config has executed
		return false;
	} else if (!gtmStartedEvent['gtm.uniqueEventId']) {
		// GTM inline config has ran, but main GTM js is not loaded (likely AdBlock, NoScript, URL blocking etc.)
		return false;
	}
	return true; // GTM is fully loaded and working
}

/**
 * Function implements a guard for a callback, and executes it if the GTM callback is to be
 * invoked after the threshold time;
 *
 * If the GTM is disabled, the `callback` is invoked immediately
 *
 * @param callback - callback function, which shall be executed after the GTM event is flushed
 * @param threshold - threshold in ms; maximum time after which the callback will be executed
 * @returns {function} trackingFunction(`trackingFunction`, `...trackingArgs`)
 * @param trackingFunction - reference to a tracking function, which accepts callback as a last
 * parameter
 * @param trackingArgs - arguments of the tracking function
 *
 * @example
 * tryBeforeTimeout(
 * 	() => { window.location.href = response.data.redirectTo; },
 * 	2000
 * )(track_marketing_consent, basket.value, 'opt_in');
 */
export function tryBeforeTimeout(callback: () => void, threshold = 200) {
	if (!isGtmLoaded()) callback();

	let callbackExecuted = false;

	const lastResort = setTimeout(() => {
		callback();
		callbackExecuted = true;
	}, threshold);

	return (trackingFunction: (...args: any[]) => void, ...trackingArgs: any[]) =>
		trackingFunction(...trackingArgs, (containerId: string) => {
			if (callbackExecuted) return;
			if (containerId.startsWith('GTM-')) {
				clearTimeout(lastResort);
				callback();
			}
		});
}
